import React, { useLayoutEffect } from 'react';
import ReactHtmlParser from 'react-html-parser';
import ScrollMagic from 'scrollmagic';
import { useScrollController } from '@context/ScrollControllerProvider/useScrollController';
import { useIsMounted } from '@hooks/useIsMounted';
import { gsap, Linear } from 'gsap';
import { useTheme } from '@styledComponents';
import { AvatarSizes } from '@components/Avatar';
import { Flex } from '@components/Flex';
import { useThemeMediaQuery } from '@hooks/useThemeMediaQuery';
import {
  MainTextPairing,
  StyledAvatarContainer,
  StyledPasswordContainer,
  StyledPasswordImage,
  StyledPrivacyControlsContainer,
  StyledPrivateEventsContainer,
  AvatarFormCloud,
  ProtectionCloud,
  DetailTextPairing,
  TextPairingWrapper,
  ColumnContainer,
  ColumnContent,
  StyledTopLeftAvatar,
  StyledTopRightAvatar,
  StyledBottomLeftAvatar,
  StyledBottomRightAvatar,
} from './UnderWrapsSection.styles';
import { useTranslation } from 'react-i18next';

export const UnderWrapsSection = () => {
  const isMobileDevice = useThemeMediaQuery('below_sm');
  const sectionId = 'Powerful_Event_Privacy';
  const scrollController = useScrollController();
  const isMounted = useIsMounted();
  const theme = useTheme();

  const { t } = useTranslation('weddingWebsitePage');
  const underWrapsSectionData = t('sections.underWraps');

  useLayoutEffect(() => {
    let scene: InstanceType<typeof ScrollMagic.Scene> | undefined;
    let underWrapTimeline: ReturnType<typeof gsap.timeline>;

    if (scrollController && isMounted) {
      const initTimeline = () => {
        if (scrollController) {
          underWrapTimeline = gsap.timeline({ paused: true, defaults: { ease: Linear.easeNone } });
        }
      };
      const initScene = () => {
        scene = new ScrollMagic.Scene({
          triggerElement: `#${sectionId}`,
          triggerHook: 'onCenter',
          duration: '100%',
        })
          .addTo(scrollController)
          .on('progress', (event: ScrollMagic.SceneProgressEvent) => {
            underWrapTimeline?.progress(event.progress);
          });
      };
      initTimeline();
      initScene();
    }
    const clearScene = () => {
      if (underWrapTimeline) {
        underWrapTimeline.progress(0);
        underWrapTimeline.kill();
      }
      if (scene) {
        scene.destroy(true);
      }
    };
    return clearScene;
  }, [scrollController, isMounted, theme]);

  const renderZoomOutContent = () => {
    return (
      <ColumnContainer>
        <ColumnContent>
          <Flex
            style={{ width: '100%', zIndex: 2, position: 'relative' }}
            backgroundColor="neutra6"
            flexDirection={'column'}
            id={sectionId}
          >
            <StyledPrivacyControlsContainer flexDirection={'column'} marginLeft="auto" marginRight="auto" marginTop="13">
              <MainTextPairing
                eyebrowVariant="sectionEyeBrow24"
                eyebrowColor="humana3"
                eyebrow={underWrapsSectionData.header.eyebrow}
                eyebrowTagName="h2"
                titleVariant="hed96Break"
                titleColor="white"
                titleTagName="h2"
                title={underWrapsSectionData.header.title}
                bodyVariant="dek24"
                bodyColor="neutra1"
                body={isMobileDevice ? underWrapsSectionData.header.contentMobile : underWrapsSectionData.header.content}
                alignment="center"
              />
            </StyledPrivacyControlsContainer>
            <StyledPrivateEventsContainer
              flexDirection={['column-reverse', 'column-reverse', 'row']}
              alignItems="center"
              justifyContent="center"
              marginTop={[0, 11]}
            >
              <TextPairingWrapper marginRight={[0, 0, 0]} marginTop={[0, -10]}>
                <DetailTextPairing
                  titleVariant="calloutHed32"
                  titleColor="white"
                  title={ReactHtmlParser(isMobileDevice ? underWrapsSectionData.details.titleMobile : underWrapsSectionData.details.title)}
                  titleTagName="h3"
                  bodyVariant="dek20"
                  bodyColor="neutra1"
                  body={underWrapsSectionData.details.content}
                  alignment="left"
                />
              </TextPairingWrapper>
              <StyledAvatarContainer>
                <StyledTopLeftAvatar sizeVariant={AvatarSizes.sm} strokeWidth="3px" strokeColor={'white'} />
                <StyledTopRightAvatar
                  sizeVariant={isMobileDevice ? AvatarSizes.md : AvatarSizes.lg}
                  strokeWidth="4px"
                  strokeColor={'white'}
                />
                <AvatarFormCloud
                  alt={underWrapsSectionData.privateEventsAltText}
                  src={underWrapsSectionData.schedule}
                  optimization="default"
                />
                <StyledBottomLeftAvatar
                  sizeVariant={isMobileDevice ? AvatarSizes.sm : AvatarSizes.md}
                  strokeWidth="4px"
                  strokeColor={'white'}
                />
                <StyledBottomRightAvatar
                  sizeVariant={isMobileDevice ? AvatarSizes.md : AvatarSizes.lg}
                  strokeWidth="4px"
                  strokeColor={'white'}
                />
              </StyledAvatarContainer>
            </StyledPrivateEventsContainer>
            <StyledPasswordContainer flexDirection={['column', 'column', 'row']} alignItems="center" justifyContent="center">
              <StyledPasswordImage>
                <ProtectionCloud
                  alt={underWrapsSectionData.passwordProtectAltText}
                  src={underWrapsSectionData.requirePassword}
                  optimization="default"
                />
              </StyledPasswordImage>
              <TextPairingWrapper marginLeft={[0, 0, 13]} marginTop={[10, 10, 0]}>
                <DetailTextPairing
                  titleVariant="calloutHed32"
                  titleColor="white"
                  title={ReactHtmlParser(underWrapsSectionData.options.title)}
                  titleTagName="h3"
                  bodyVariant="dek20"
                  bodyColor="neutra1"
                  body={underWrapsSectionData.options.content}
                  alignment="left"
                />
              </TextPairingWrapper>
            </StyledPasswordContainer>
          </Flex>
        </ColumnContent>
      </ColumnContainer>
    );
  };
  return renderZoomOutContent();
};

export default UnderWrapsSection;
